import { useConfigStore } from '@/js/stores/configStore'
import type { IRogue } from '@/js/services/RogueTypes'

// ShipperHQ codes - used to determine a default shipping method and pickup
export const PICKUP_CODE = 'shqshared_fixed'

const regexes: { [key: string]: RegExp } = {}

/**
 * Check the postcode regex for the specified country. Useful to check before sending to ShipperHQ etc.
 * Not all countries have a regex saved.
 */
export function validatePostcode(rogue: IRogue, countryId: string, postcode: string) {
  if (!postcode) {
    return false
  }

  if (!rogue.pinia) {
    // Pinia should never be undefined. If it is, this is a bug that needs to be fixed at the source.
    const error = new Error('[validatePostcode] Pinia is undefined/null. This should never happen. Please investigate the root cause.')
    rogue.log.error('[validatePostcode] Pinia is undefined/null. This should never happen. Please investigate the root cause.', error, {
      countryId,
      postcode,
    })
    
    return false
  }

  if (countryId) {
    const configStore = useConfigStore(rogue.pinia)
    const country = configStore.countries.find((c: any) => c.alpha2 === countryId)

    if (country && country.postcodeRegex) {
      if (!regexes[country.postcodeRegex]) {
        regexes[country.postcodeRegex] = new RegExp(country.postcodeRegex)
      }

      if (regexes[country.postcodeRegex].exec(postcode) === null) {
        return false
      }
    }
  }

  return true
}

/**
 *  Check the primary address fields - country, postcode, region (depending on country), city, and street
 */
export function validateAddress(rogue: IRogue, address: Record<string, any> | null | undefined, validateStreet = true) {
  if (!address || typeof address !== 'object') {
    rogue?.log.error('validateAddress: Invalid address provided (null, undefined, or non-object)', new Error('Invalid address type'), {
      addressType  : typeof address,
      addressValue : address,
      validateStreet,
    })
    
    return false
  }

  const { countryId, postcode, region, city, street } = address
  if (!city || !countryId || !postcode) {
    return false
  }

  if (!region) {
    const configStore = useConfigStore(rogue.pinia!)
    const country = configStore.countries.find((c: any) => c.alpha2 === countryId)
    if (!country || (country?.stateLabel && typeof country.stateLabel === 'string') || country.requireRegion) {
      return false
    }
  }

  if (validateStreet && (!street || street.filter((s: any) => Boolean(s)).length === 0)) {
    return false
  }

  return validatePostcode(rogue, countryId, postcode)
}

